import type { FetchError } from 'ofetch'
import type { AsyncData } from 'nuxt/app'

export const useAuthStore = defineStore('auth', () => {
  const { $api } = useNuxtApp()
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const accessTokenExpires = ref<number | null>(null)
  const refreshTokenExpires = ref<number | null>(null)
  const password = ref<string | null>(null)
  const passwordRepeat = ref<string | null>(null)
  const isRefreshing = ref<boolean>(false)

  const setAuthData = ({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken,
    accessTokenExpires: newAccessTokenExpires,
    refreshTokenExpires: newRefreshTokenExpires,
    userData
  } : {accessToken: string, refreshToken: string, accessTokenExpires: number, refreshTokenExpires: number, userData?: any}) => {
    const userStore = useUserStore()
    accessToken.value = newAccessToken
    refreshToken.value = newRefreshToken
    accessTokenExpires.value = newAccessTokenExpires
    refreshTokenExpires.value = newRefreshTokenExpires

    if (userData) {
      userStore.setUserData(userData)
    }
  }

  const login = async ({ email, password } : {email: string, password: string}) => {
    const { $bus } = useNuxtApp()
    const data = await $api('auth/login', {
      method: 'POST',
      body: { email, password },
      recaptchaKey: 'login'
    }) as AuthLoginSuccess

    if (data) {
      setAuthData({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpires: data.accessTokenExpires,
        refreshTokenExpires: data.refreshTokenExpires,
        userData: data.data
      })

      $bus.$emit('user-id', { userId: data.data.id })
    }

    return data
  }

  const register = async ({ name, email, password, passwordRepeat } : {name: string, email: string, password:string, passwordRepeat: string}) => {
    return await $api('customers', {
      method: 'POST',
      body: {
        name,
        email,
        password,
        passwordConfirmation: passwordRepeat
      }
    })
  }

  const activate = async ({ token }: {token: string}) => {
    const data = await $api('auth/activation', {
      method: 'POST',
      body: { token }
    }) as AuthLoginSuccess

    if (data) {
      setAuthData({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpires: data.accessTokenExpires,
        refreshTokenExpires: data.refreshTokenExpires,
        userData: data.data
      })
    }

    return data
  }

  const doRefreshToken = async () => {
    const baseURL = useRuntimeConfig().public.baseURL
    // Prevents two refresh call at the same time.
    if (isRefreshing.value) { return }

    try {
      if (!refreshToken.value || !refreshTokenExpires.value || refreshTokenExpires.value * 1000 < Date.now()) { return }
      isRefreshing.value = true

      const data: AuthLoginSuccess = await $fetch( // Using raw fetch here to prevent conflicts with global auth checks
        'auth/token',
        {
          baseURL,
          method: 'POST',
          body: { token: refreshToken.value }
        }
      )

      if (data) {
        setAuthData({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpires: data.accessTokenExpires,
          refreshTokenExpires: data.refreshTokenExpires
        })

        setTimeout(() => {
          isRefreshing.value = false
        }, 2000)

        return data.accessToken
      }
    } catch (e) {
      isRefreshing.value = false
    } finally {
      isRefreshing.value = false
    }
  }

  const getValidAccessToken = async () => {
    if (isRefreshing.value) { return }

    try {
      // accessToken en refreshToken are not set, can not be refreshed so return
      if (!accessToken.value && !refreshToken.value) {
        return
      }

      // accessToken is valid(not expired), return this
      if (
        accessToken.value &&
        accessTokenExpires.value &&
        accessTokenExpires.value * 1000 > Date.now()
      ) {
        return accessToken.value
      }

      // refreshToken is valid(not expired), try to refresh. on success return accessToken
      if (
        refreshToken.value &&
        refreshTokenExpires.value &&
        refreshTokenExpires.value * 1000 > Date.now()
      ) {
        const newAccessToken = await doRefreshToken()
        // refresh successfull: accessToken is valid, return this
        if (newAccessToken) {
          return newAccessToken
        }
      }
      // refresh failed or refreshToken is expired, reset state so refresh is not tried again
      return await resetToken()
    } catch (e) {
      // throw new Error(e.message)
    }
  }

  const resetToken = () => {
    accessToken.value =
      accessTokenExpires.value =
      refreshToken.value =
      refreshTokenExpires.value = null
  }

  const logout = async () => {
    try {
      const response = await $api('auth/logout', { method: 'POST', body: { token: refreshToken.value } })
      return response
    } finally {
      resetToken()
      const { reset } = useWishlistStore()
      reset()
    }
  }

  const forgotPassword = async ({ email } : {email: string}) => {
    const response = await $api('auth/forgotpassword', {
      method: 'POST',
      body: { email },
      recaptchaKey: 'forgotpassword'
    })
    return response
  }

  const resetPassword = async ({ token, password, passwordRepeat } : {token: string, password:string, passwordRepeat:string}) => {
    const data = await $api('auth/resetpassword', {
      method: 'POST',
      body: {
        token,
        password,
        passwordConfirmation: passwordRepeat
      }
    }) as AuthLoginSuccess

    if (data) {
      setAuthData({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        accessTokenExpires: data.accessTokenExpires,
        refreshTokenExpires: data.refreshTokenExpires
      })
    }
  }

  const loggedIn = computed(() => {
    const expiredAccessToken = accessTokenExpires.value && (accessTokenExpires.value < Math.floor(Date.now() / 1000))
    const expiredRefreshToken = refreshTokenExpires.value && (refreshTokenExpires.value < Math.floor(Date.now() / 1000))
    return (
      (accessToken.value && accessToken.value !== undefined && accessToken.value !== null && !expiredAccessToken) ||
      (refreshToken.value && refreshToken.value !== undefined && refreshToken.value !== null && !expiredRefreshToken)
    )
  })

  return {
    accessToken,
    refreshToken,
    accessTokenExpires,
    refreshTokenExpires,
    password,
    passwordRepeat,
    loggedIn,
    isRefreshing,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    setAuthData,
    activate,
    doRefreshToken,
    getValidAccessToken,
    resetToken
  }
}, {
  persistedState: {
    persist: true,
    includePaths: ['accessToken', 'refreshToken', 'accessTokenExpires', 'refreshTokenExpires'],
    beforeHydrate: oldState => {
      delete oldState.isRefreshing
    }
  }
})
